import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { toast } from '@/hooks/use-toast';
import { apiErrorMessage } from '../api-error';
import {
  answersForStep,
  changedAnswers,
  draftFromReport,
  hasAnyAnswer,
  rebaseDraft,
  type ReportAnswers,
  type ReportDraft,
  type ReportOutsideFactor,
  type ReportStep,
  type SavedReport,
} from './report-questions';

/**
 * The after action report: one form's state, shared by the Debrief tab and the end-flow wizard.
 *
 * The two shells are two instances of this hook over one query, so they cannot disagree about
 * what is saved. Each keeps its own draft; what is saved is the single source of truth, and
 * the draft is reset to it whenever it changes.
 *
 * The saved report is written into the cache from the PUT response rather than refetched. The
 * wizard saves a step and moves straight on, and a refetch landing a moment later would reset
 * the draft under whatever the user had started typing in the next step.
 */
export const useExperimentReport = (experimentId: number) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const queryKey = ['EXPERIMENT_REPORT', experimentId];

  const { data: report, isLoading } = useQuery<SavedReport | null>({
    enabled: !!experimentId,
    queryKey,
    queryFn: () => apiClient.get<SavedReport | null>(`/experiments/${experimentId}/report`),
  });

  const saved = draftFromReport(report);
  const savedKey = JSON.stringify(saved);

  const [draft, setDraft] = useState<ReportDraft>(saved);
  const [lastSaved, setLastSaved] = useState<ReportDraft>(saved);
  const [isSaving, setIsSaving] = useState(false);

  // Adjusting state when the saved report changes, rather than in an effect: it runs during
  // render, so nothing is shown from the outdated draft first. Compared by content, so an
  // ordinary refetch of an unchanged report does not discard what someone is typing, and
  // rebased, so a save landing mid-keystroke keeps whatever was typed since it was sent.
  // The hook is keyed by experiment where it is mounted, so a draft never crosses experiments.
  if (savedKey !== JSON.stringify(lastSaved)) {
    setLastSaved(saved);
    setDraft(rebaseDraft({ draft, previous: lastSaved, next: saved }));
  }

  const setAnswer = <K extends keyof ReportDraft>(field: K, value: ReportDraft[K]) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const toggleFactor = (factor: ReportOutsideFactor) =>
    setDraft((current) => ({
      ...current,
      outsideFactors: current.outsideFactors.includes(factor)
        ? current.outsideFactors.filter((item) => item !== factor)
        : [...current.outsideFactors, factor],
    }));

  const pending = changedAnswers(draft, saved);
  const isDirty = Object.keys(pending).length > 0;

  /**
   * Sends only the answers given, as a patch. The server's rejection message is surfaced
   * verbatim, as for the schedule: each names the field and says why. Resolves false rather
   * than throwing, so a wizard step can stay put on failure without a try/catch of its own.
   */
  const saveAnswers = async (answers: ReportAnswers) => {
    if (Object.keys(answers).length === 0) return true;

    setIsSaving(true);
    try {
      const wasFirstSave = !report;
      const next = await apiClient.put<SavedReport>(`/experiments/${experimentId}/report`, answers);
      queryClient.setQueryData(queryKey, next);
      queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
      // The save that creates the report records its author on the experiment, which is
      // where the Debrief tab's badge reads it from. Only then: a refetch per wizard step
      // would reload the whole experiment for nothing.
      if (wasFirstSave) queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', experimentId] });
      // The home screen may be asking for this very debrief; a saved answer ends the request.
      queryClient.invalidateQueries({ queryKey: ['ATTENTION'] });
      return true;
    } catch (error) {
      toast({ title: 'Could not save the report', description: apiErrorMessage(error) });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const save = () => saveAnswers(pending);

  /**
   * One modal screen. Nothing answered means nothing is sent, so stepping through stays free.
   * `changes` are answers the screen worked out itself (the outside factors a confirmed row
   * implies) and which the draft has not caught up with yet, since state set in the same event
   * is not visible until the next render.
   */
  const saveStep = (step: ReportStep, changes: Partial<ReportDraft> = {}) =>
    saveAnswers(answersForStep(step, changedAnswers({ ...draft, ...changes }, saved)));

  const discard = () => setDraft(saved);

  return {
    report: report ?? null,
    author: report?.author ?? null,
    savedAt: report?.updatedAt ?? null,
    isLoading,
    draft,
    setAnswer,
    toggleFactor,
    isDirty,
    isSaving,
    hasReport: !!report,
    hasAnswers: hasAnyAnswer(saved),
    save,
    saveStep,
    discard,
  };
};
